import { useQuery } from "@tanstack/react-query";
import { MetricProgressTable } from "components/marketing-goals/metric-progress-table";
import {
    trackedMetrics,
    trackedMetricTarget,
} from "components/marketing-goals/use-metric-progress";
import { trpc } from "components/rpc/trpc";
import { useActivityTypeOptionsState } from "components/tasks/activity-type-options";
import { Spinner } from "components/ui/spinner";
import { TooltipLabel } from "components/ui/tooltip-label";
import React from "react";
import { useNow } from "store/selectors/hooks";
import { currentYearMonth, ymLabel } from "../marketing-goals/pace";

export const MarketingPlanProjectionSummary: React.FC<unknown> = () => {
    const now = useNow();
    const trpcClient = trpc.useTRPC();
    const { options: activityTypeOptions, isLoading: optionsLoading } =
        useActivityTypeOptionsState();
    const month = currentYearMonth(now);

    const attainmentQuery = useQuery({
        ...trpcClient.marketingGoal.myAttainment.queryOptions({ month }),
    });

    const isLoading = attainmentQuery.isLoading || optionsLoading;
    const goal = attainmentQuery.data?.goal ?? null;
    const goalMetrics = goal
        ? trackedMetrics.filter(
              metric => trackedMetricTarget(goal, activityTypeOptions, metric) > 0,
          )
        : [];

    if (!isLoading && !attainmentQuery.isError && goalMetrics.length === 0) {
        return null;
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-1.5">
                <h3 className="twoverride text-sm font-semibold text-gray-800">
                    Projected progress this month
                    <span className="font-normal text-gray-500"> · {ymLabel(month)}</span>
                </h3>
                <TooltipLabel
                    align="center"
                    tooltip="Goals are set by your agency's administrators. A touchpoint counts toward projected Visits only if it's visit-only. If it combines visit with call, text, or email, its full projected volume counts toward Other touchpoints instead."
                />
            </div>
            {isLoading ? (
                <div className="flex justify-center py-4">
                    <Spinner />
                </div>
            ) : attainmentQuery.isError ? (
                <p className="text-sm text-red-600">
                    We couldn&apos;t load your plan projection. Please try again.
                </p>
            ) : (
                <MetricProgressTable metrics={goalMetrics} density="compact" />
            )}
        </div>
    );
};
